import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Coffee, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";

const STEPS = [
  {
    id: "drinkers",
    overline: "Step 1 — Your Team",
    title: "How many coffee drinkers are we fuelling?",
    options: ["1–5", "6–15", "16–50", "50+"],
  },
  {
    id: "roast",
    overline: "Step 2 — Your Taste",
    title: "Which roast does your team lean towards?",
    options: ["Light", "Medium", "Dark"],
  },
  {
    id: "budget",
    overline: "Step 3 — Your Budget",
    title: "What's your monthly coffee budget?",
    options: ["₹2,000–5,000", "₹5,000–15,000", "₹15,000–50,000", "₹50,000+"],
  },
];

export default function CoffeeConcierge() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [business, setBusiness] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const pick = (id, value) => {
    setAnswers((a) => ({ ...a, [id]: value }));
    setTimeout(() => setStep((s) => s + 1), 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!business.trim()) return toast.error("Please enter your business name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email.");
    if (!phone.trim()) return toast.error("Please enter a contact number.");
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/concierge/lead`, {
        business,
        email,
        phone,
        drinkers: answers.drinkers,
        roast: answers.roast,
        budget: answers.budget,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const current = STEPS[step];

  return (
    <section id="concierge" className="py-20 md:py-28 px-6 md:px-12 bg-[#2D372B]">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-start">
        <div>
          <div className="overline !text-[#A9B39D] mb-4">Coffee Concierge</div>
          <h2 className="font-serif-display text-4xl md:text-5xl font-medium tracking-tighter text-[#F7F5F0] leading-[1.02]">
            Coffee for your office, <span className="italic text-[#D19B7B]">curated.</span>
          </h2>
          <p className="mt-6 max-w-md text-[#C9CFC0] leading-relaxed">
            Tell us about your team in three quick steps and receive a tailored,
            no-obligation quote — freshly roasted beans, delivered on your schedule.
          </p>
          <div className="mt-8 flex gap-1.5">
            {[...STEPS, { id: "contact" }].map((s, i) => (
              <span
                key={s.id}
                className={`h-1 w-10 rounded-full transition-colors ${i <= step ? "bg-[#D19B7B]" : "bg-[#F7F5F0]/15"}`}
              />
            ))}
          </div>
        </div>

        <div data-testid="concierge-card" className="bg-[#F7F5F0] rounded-3xl p-8 md:p-10 min-h-[360px] flex flex-col">
          {done ? (
            <div data-testid="concierge-success" className="m-auto text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-[#89937C]/15 flex items-center justify-center mb-4">
                <Check size={22} className="text-[#89937C]" />
              </div>
              <h3 className="font-serif-display text-3xl text-[#2D372B] mb-2">Consider it brewing.</h3>
              <p className="text-sm text-[#5C665A] max-w-sm mx-auto">
                Thank you, {business}. Your tailored quote is being prepared — Coach Sanjay
                will reach you at <span className="text-[#2D372B] font-medium">{email}</span> within 24 hours.
              </p>
            </div>
          ) : step < STEPS.length ? (
            <>
              <div className="overline mb-2">{current.overline}</div>
              <h3 data-testid="concierge-step-title" className="font-serif-display text-2xl md:text-3xl text-[#2D372B] mb-6">{current.title}</h3>
              <div className={`grid gap-3 ${current.options.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                {current.options.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    data-testid={`concierge-option-${current.id}-${opt.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                    onClick={() => pick(current.id, opt)}
                    className={`px-5 py-4 rounded-2xl border text-[#2D372B] transition-all text-center ${
                      answers[current.id] === opt
                        ? "border-[#AD6A48] ring-2 ring-[#AD6A48]/25 bg-white"
                        : "border-[#E5E2D9] bg-white hover:border-[#AD6A48]/60"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button
                  type="button"
                  data-testid="concierge-back-btn"
                  onClick={() => setStep(step - 1)}
                  className="mt-auto pt-6 text-sm text-[#5C665A] hover:text-[#2D372B] inline-flex items-center gap-1 self-start"
                >
                  <ArrowLeft size={14} /> Back
                </button>
              )}
            </>
          ) : (
            <form onSubmit={submit} className="flex flex-col h-full">
              <div className="overline mb-2">Final Step — Your Quote</div>
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#2D372B] mb-2">Where do we send it?</h3>
              <p className="text-xs text-[#5C665A] mb-5">
                {answers.drinkers} drinkers · {answers.roast} roast · {answers.budget}/month
              </p>
              <div className="space-y-3">
                <Input data-testid="concierge-business-input" value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="Business name" className="bg-white border-[#E5E2D9] h-12" />
                <Input data-testid="concierge-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" className="bg-white border-[#E5E2D9] h-12" />
                <Input data-testid="concierge-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Contact number" className="bg-white border-[#E5E2D9] h-12" />
              </div>
              <button
                data-testid="concierge-submit-btn"
                type="submit"
                disabled={loading}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#AD6A48] transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Coffee size={16} />}
                Get my tailored quote
              </button>
              <button
                type="button"
                data-testid="concierge-back-btn"
                onClick={() => setStep(step - 1)}
                className="mt-4 text-sm text-[#5C665A] hover:text-[#2D372B] inline-flex items-center gap-1 self-start"
              >
                <ArrowLeft size={14} /> Back
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
